import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Project, ProjectStatus } from '@/types/project';
import type { LifecycleStageBuild, LifecycleStageTender } from '@/constants/lifecycleStages';
import { useAuth } from './useAuth';
import { useEffect } from 'react';
import { dbLogger } from '@/lib/structuredLogger';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

export interface ProjectFiltersData {
  searchQuery: string;
  projectGroup: string;
  contractYear?: string[];
  projectType: string[];
  teamLead: string;
  status: string[];
  company: string;
  projectRoleUserIds?: string[]; // Filter by users who have project_roles (multi-select)
  projectMark?: string; // Filter by project mark (prio_1, prio_2, decent)
  projectRoleTypes?: string[]; // Filter by specific role types (multi-select)
  lifecycleStageBuild?: LifecycleStageBuild | 'unset' | ''; // Filter by build lifecycle stage (or 'unset' for null)
  lifecycleStageTender?: LifecycleStageTender | 'unset' | ''; // Filter by tender lifecycle stage (or 'unset' for null)
}

interface OptimizedProjectResponse {
  projects: Project[];
  totalCount: number;
  executionTime: number;
}

interface GetProjectsOptimizedResult {
  success: boolean;
  data?: {
    projects: Project[];
    total_count: number;
  };
  error?: string;
  meta?: {
    execution_time_ms: number;
  };
}

// Flatten query keys for better performance (no JSON.stringify)
const buildQueryKey = (filters: ProjectFiltersData, page: number = 1) => [
  'projects-optimized-v2',
  filters.searchQuery || '',
  filters.projectGroup || '',
  (filters.contractYear || []).join(',') || '',
  filters.projectType.join(',') || '',
  filters.teamLead || '',
  filters.status.join(',') || '',
  filters.company || '',
  (filters.projectRoleUserIds || []).join(',') || '',
  filters.projectMark || '',
  (filters.projectRoleTypes || []).join(',') || '',
  filters.lifecycleStageBuild || '',
  filters.lifecycleStageTender || '',
  page.toString()
].filter(Boolean);

// Input sanitization
const sanitizeFilters = (filters: ProjectFiltersData): ProjectFiltersData => {
  return {
    searchQuery: filters.searchQuery?.replace(/[<>'"&]/g, '').slice(0, 100).trim() || '',
    projectGroup: filters.projectGroup || '',
    contractYear: filters.contractYear || [],
    projectType: filters.projectType || [],
    teamLead: filters.teamLead || '',
    status: filters.status || [],
    company: filters.company || '',
    projectRoleUserIds: filters.projectRoleUserIds || [],
    projectMark: filters.projectMark || '',
    projectRoleTypes: filters.projectRoleTypes || [],
    lifecycleStageBuild: filters.lifecycleStageBuild || '',
    lifecycleStageTender: filters.lifecycleStageTender || ''
  };
};

export const useOptimizedProjectsQuery = (
  filters: ProjectFiltersData,
  page: number = 1,
  pageSize: number = 12
) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const sanitizedFilters = sanitizeFilters(filters);
  
  const queryKey = buildQueryKey(sanitizedFilters, page);
  
  // Background prefetching for next page
  useEffect(() => {
    const prefetchNext = async () => {
      // Only prefetch if we have a current result and there might be more data
      const currentQueryData = queryClient.getQueryData(queryKey) as OptimizedProjectResponse | undefined;
      const currentPageItems = currentQueryData?.projects?.length || 0;
      
      // Skip prefetching if current page has less than pageSize items (no next page)
      if (currentPageItems < pageSize) return;
      
      const nextPageKey = buildQueryKey(sanitizedFilters, page + 1);
      const hasNextPageCached = queryClient.getQueryData(nextPageKey);
      
      if (!hasNextPageCached) {
        queryClient.prefetchQuery({
          queryKey: nextPageKey,
          queryFn: () => fetchOptimizedProjects(sanitizedFilters, page + 1, pageSize),
          staleTime: 5 * 60 * 1000 // 5 minutes
        });
      }
    };
    
    // Prefetch after 100ms to not block current request
    const timer = setTimeout(prefetchNext, 100);
    return () => clearTimeout(timer);
  }, [page, sanitizedFilters, pageSize, queryClient, queryKey]);

  const hasCachedData = !!queryClient.getQueryData(queryKey);

  return useQuery({
    queryKey,
    queryFn: () => fetchOptimizedProjects(sanitizedFilters, page, pageSize),
    enabled: !!user?.id || hasCachedData,
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnMount: false, // Use cached data for instant render, refetch in background
    refetchOnWindowFocus: false, // Prevent excessive refetching on window focus
    placeholderData: keepPreviousData, // Instant render with cached data while refetching (v5 syntax)
  });
};

const fetchOptimizedProjects = async (
  filters: ProjectFiltersData,
  page: number,
  pageSize: number
): Promise<OptimizedProjectResponse> => {
  const startTime = performance.now();
  
  try {
    // Log filter operation
    dbLogger.info('Projects query started', {
      filters,
      page,
      pageSize,
      timestamp: Date.now()
    });
    
    const offset = (page - 1) * pageSize;
    
    // Try the optimized RPC function first (pass first user ID if multi-select has values)
    const { data, error } = await supabase.rpc('get_projects_optimized', {
      search_query: filters.searchQuery || null,
      project_group_filter: filters.projectGroup || null,
      project_type_filter: filters.projectType.length > 0 ? filters.projectType[0] : null,
      status_filters: filters.status.length > 0 ? filters.status : null,
      team_lead_filter: filters.teamLead || null,
      company_filter: filters.company || null,
      contract_year_filter: (filters.contractYear && filters.contractYear.length > 0 ? filters.contractYear : null) as any,
      page_offset: offset,
      page_limit: pageSize,
      p_project_role_user_id: filters.projectRoleUserIds && filters.projectRoleUserIds.length > 0 ? filters.projectRoleUserIds[0] : null
    });
    
    if (error) {
      dbLogger.warn('RPC query failed, falling back to direct query', { 
        error: error.message, 
        code: error.code,
        filters,
        isPreviewMode: filters.teamLead?.startsWith?.('preview-user-') || false
      });
      
      // Fallback to direct query
      return await fetchProjectsFallback(filters, page, pageSize, startTime);
    }
    
    const result = data as unknown as GetProjectsOptimizedResult;
    const queryTime = performance.now() - startTime;
    
    if (!result.success) {
      dbLogger.warn('RPC returned error, falling back to direct query', { error: result.error, filters });
      return await fetchProjectsFallback(filters, page, pageSize, startTime);
    }
    
    // Safety net - filter out any service/inactive that slipped through
    let allProjects = result.data?.projects || [];
    const disallowedStatuses = new Set(['service', 'inactive']);
    let projects = allProjects.filter((p: Project) => 
      !disallowedStatuses.has(p.status?.toLowerCase())
    );

    const removedCount = allProjects.length - projects.length;
    if (removedCount > 0) {
      dbLogger.warn('⚠️ RPC returned excluded statuses, client-filtering applied', {
        removedCount,
        sampleStatuses: allProjects.slice(0, 5).map((p: Project) => p.status),
        filters
      });
    }

    // Client-side filter for project_mark (RPC doesn't support it)
    if (filters.projectMark) {
      projects = projects.filter((p: Project) => p.project_mark === filters.projectMark);
    }

    // Client-side filter for lifecycle_stage_build (RPC doesn't support it)
    if (filters.lifecycleStageBuild === 'unset') {
      projects = projects.filter((p: Project) => p.lifecycle_stage_build == null);
    } else if (filters.lifecycleStageBuild) {
      projects = projects.filter((p: Project) => p.lifecycle_stage_build === filters.lifecycleStageBuild);
    }

    // Client-side filter for lifecycle_stage_tender (RPC doesn't support it)
    if (filters.lifecycleStageTender === 'unset') {
      projects = projects.filter((p: Project) => (p as any).lifecycle_stage_tender == null);
    } else if (filters.lifecycleStageTender) {
      projects = projects.filter((p: Project) => (p as any).lifecycle_stage_tender === filters.lifecycleStageTender);
    }

    // Client-side filter for project_role_types (multi-select, RPC doesn't support it)
    if (filters.projectRoleTypes && filters.projectRoleTypes.length > 0) {
      // Need to fetch project IDs with any of these role types
      const { data: roleTypeProjectIds } = await supabase
        .from('project_user_permissions')
        .select('project_id')
        .overlaps('project_roles', filters.projectRoleTypes);
      
      if (roleTypeProjectIds && roleTypeProjectIds.length > 0) {
        const projectIdsSet = new Set(roleTypeProjectIds.map(p => p.project_id));
        projects = projects.filter((p: Project) => projectIdsSet.has(p.id));
      } else {
        projects = [];
      }
    }

    // Hybrid count: dùng total_count từ RPC khi không có client-side filter
    // (project_mark / lifecycle_stage_build / lifecycle_stage_tender / project_role_types
    //  được lọc CLIENT — RPC không biết, nên count sẽ overcount → fallback projects.length)
    const hasClientSideFilter =
      !!filters.projectMark ||
      !!filters.lifecycleStageBuild ||
      !!filters.lifecycleStageTender ||
      (filters.projectRoleTypes?.length ?? 0) > 0;

    const adjustedTotalCount = hasClientSideFilter
      ? projects.length
      : (result.data?.total_count ?? projects.length);

    dbLogger.debug('RPC response processed', {
      totalCount: adjustedTotalCount,
      projectsReceived: projects.length,
      clientFiltered: removedCount,
      filters,
      sampleStatuses: projects.slice(0, 5).map((p: Project) => p.status)
    });
    
    // Log successful operation
    dbLogger.info('Projects query completed (RPC)', {
      filters,
      totalCount: adjustedTotalCount,
      projectsReturned: projects.length,
      clientFiltered: removedCount,
      queryTime,
      serverExecutionTime: result.meta?.execution_time_ms
    });
    
    return {
      projects,
      totalCount: adjustedTotalCount,
      executionTime: queryTime
    };
    
  } catch (error) {
    const queryTime = performance.now() - startTime;
    dbLogger.warn('Projects query error, falling back to direct query', {
      error: error instanceof Error ? error.message : 'Unknown error',
      filters,
      queryTime
    });
    
    // Fallback to direct query on any error
    return await fetchProjectsFallback(filters, page, pageSize, startTime);
  }
};

// Fallback function for direct project queries
const fetchProjectsFallback = async (
  filters: ProjectFiltersData,
  page: number,
  pageSize: number,
  startTime: number
): Promise<OptimizedProjectResponse> => {
  const offset = (page - 1) * pageSize;
  
  // Build base query
  let query = supabase
    .from('projects')
    .select(`
      id,
      project_name,
      description,
      image_url,
      status,
      project_type,
      project_group,
      company,
      is_active,
      created_at,
      updated_at,
      user_id,
      proc_file,
      doc_file,
      shop_file,
      cost_file,
      project_folder,
      build_folder,
      doc_folder,
      req_folder,
      proposal_folder,
      tech_folder,
      report_folder,
      specs_folder,
      po_folder,
      shop_folder,
      new_project_folder,
      new_specs_folder,
      new_shop_folder,
      new_po_folder,
      new_doc_folder,
      new_cost_folder,
      new_build_folder,
      new_tech_folder,
      new_req_folder,
      new_proposal_folder,
      project_mark,
      lifecycle_stage_build,
      lifecycle_stage_tender,
      gross_floor_area_m2
    `, { count: 'exact' })
    .eq('is_active', true);
  
  // Apply filters
  if (filters.searchQuery) {
    query = query.ilike('project_name', `%${filters.searchQuery}%`);
  }
  if (filters.projectGroup) {
    query = query.eq('project_group', filters.projectGroup);
  }
  if (filters.projectType.length > 0) {
    // Note: projectType is a fixed enum (~5 values max), safe to use .in()
    // eslint-disable-next-line no-restricted-syntax
    query = query.in('project_type', filters.projectType);
  }
  
  // ALWAYS exclude inactive and service - they have dedicated pages
  query = query.not('status', 'in', '(inactive,service)');
  
  // Then apply additional status filter if provided
  if (filters.status.length > 0) {
    // Note: status is a fixed enum (7 values max), safe to use .in()
    // eslint-disable-next-line no-restricted-syntax
    query = query.in('status', filters.status as ProjectStatus[]);
  }
  
  if (filters.company) {
    if (filters.company === 'Unassigned') {
      query = query.is('company', null);
    } else {
      query = query.eq('company', filters.company);
    }
  }
  
  // Filter by project mark
  if (filters.projectMark) {
    query = query.eq('project_mark', filters.projectMark);
  }

  // Filter by build lifecycle stage
  if (filters.lifecycleStageBuild === 'unset') {
    query = query.is('lifecycle_stage_build', null);
  } else if (filters.lifecycleStageBuild) {
    query = query.eq('lifecycle_stage_build', filters.lifecycleStageBuild);
  }

  // Filter by tender lifecycle stage
  if (filters.lifecycleStageTender === 'unset') {
    query = (query as any).is('lifecycle_stage_tender', null);
  } else if (filters.lifecycleStageTender) {
    query = (query as any).eq('lifecycle_stage_tender', filters.lifecycleStageTender);
  }
  
  // Filter by project role users (multi-select) - get project IDs where any of these users have project roles
  if (filters.projectRoleUserIds && filters.projectRoleUserIds.length > 0) {
    // Using chunked query for user IDs
    const userProjectIds = await chunkedInQuerySimple<{ project_id: string }>(
      supabase,
      'project_user_permissions',
      'user_id',
      filters.projectRoleUserIds,
      'project_id',
      (q) => q.not('project_roles', 'eq', '{}')
    );
    
    if (userProjectIds && userProjectIds.length > 0) {
      const projectIds = [...new Set(userProjectIds.map(p => p.project_id))];
      // Note: This inner .in() is filtered by projectIds which comes from a controlled set
      // eslint-disable-next-line no-restricted-syntax
      query = query.in('id', projectIds);
    } else {
      // No users have project roles, return empty
      return {
        projects: [],
        totalCount: 0,
        executionTime: performance.now() - startTime
      };
    }
  }
  
  // Filter by project role types (multi-select)
  if (filters.projectRoleTypes && filters.projectRoleTypes.length > 0) {
    const { data: roleTypeProjectIds } = await supabase
      .from('project_user_permissions')
      .select('project_id')
      .overlaps('project_roles', filters.projectRoleTypes);
    
    if (roleTypeProjectIds && roleTypeProjectIds.length > 0) {
      const projectIds = [...new Set(roleTypeProjectIds.map(p => p.project_id))];
      // Note: This .in() is filtered by projectIds from a controlled set (not user input)
      // eslint-disable-next-line no-restricted-syntax
      query = query.in('id', projectIds);
    } else {
      // No projects with these role types, return empty
      return {
        projects: [],
        totalCount: 0,
        executionTime: performance.now() - startTime
      };
    }
  }
  
  // Apply pagination and ordering
  query = query
    .order('updated_at', { ascending: false })
    .range(offset, offset + pageSize - 1);
  
  const { data: projects, error, count } = await query;
  
  if (error) {
    // Handle specific error codes gracefully
    if (error.code === 'PGRST103' || error.message?.includes('416')) {
      // Range not satisfiable - return empty result instead of throwing
      dbLogger.info('Range not satisfiable, returning empty result', { filters, offset, pageSize });
      return {
        projects: [],
        totalCount: count || 0,
        executionTime: performance.now() - startTime
      };
    }
    
    dbLogger.error('Fallback projects query failed', { error: error.message, filters });
    throw error;
  }
  
  const queryTime = performance.now() - startTime;
  
  dbLogger.info('Fallback projects query completed', {
    filters,
    totalCount: count || 0,
    projectsReturned: projects?.length || 0,
    queryTime
  });
  
  return {
    projects: projects || [],
    totalCount: count || 0,
    executionTime: queryTime
  };
};